import { useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { Meter } from "./ui";
import { HsAdminGate } from "./HsAdminGate";
import VisitModal from "./VisitModal";
import AdminDayModal from "./AdminDayModal";
import type { DayEditTarget } from "./AdminDayModal";
import DayPlannerModal from "./DayPlannerModal";
import type { PlannerTarget } from "./DayPlannerModal";
import { initials, utilColour } from "./ConsultantsScreen";
import { dayCellsFor, useConsultantVisits, useActiveConsultants, toISODate, addDays, sameDay, roomForSecond, isPastDay, daysUtilisation, dayExceptionFor, useVisitsByConsultant } from "./data";
import {
    scoreVisit,
    fmtPoints,
    UNRATED_LABEL,
    buildOccasions,
    availablePoints,
    fieldDaysPerMonth,
} from "./points";
import type { PointsConfig, OccasionVisit } from "./points";
import { usePointsConfig } from "./pointsConfig";
import type { WeeklyVisitSummary, VisitConsultantT, SfVisit, DayException } from "./data";

// The trailing column carries the points total and the pairing count, both of
// which are part of the points feature, so the track goes with them.
const gridFor = (withPoints: boolean) =>
    withPoints ? "190px repeat(5, minmax(0, 1fr)) 92px" : "190px repeat(5, minmax(0, 1fr))";


/** A band of rows on the board. Grouped by team; "No team" is a real group. */
interface ConsultantGroup {
    name: string;
    consultants: VisitConsultantT[];
}

const statusStyle = (s?: string): { bg: string; fg: string } => {
    switch ((s || "").toLowerCase()) {
        case "completed":
            return { bg: T.greenTint, fg: T.green };
        case "booked":
            return { bg: T.purpleTint, fg: T.purpleDeep };
        case "closed":
            return { bg: "#F1F3F9", fg: T.muted };
        case "automation proposal":
            return { bg: T.amberTint, fg: T.amber };
        default:
            return { bg: "#F1F3F9", fg: T.muted };
    }
};

const fmtTime = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const fmtDur = (mins?: number | null) => {
    if (mins == null || isNaN(mins) || mins <= 0) return "";
    const m = Math.round(mins);
    return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};

// One visit chip: client · type · booked window · actual times/duration when logged.
/**
 * Points for one consultant on one day. Groups the day's visits into occasions
 * (same account) so several reports at one site score the highest base plus a
 * supplement, rather than being added up. Remote visits sit outside occasions
 * and score individually.
 *
 * Returns recognition (what gets reported) and capacity (what the day consumes,
 * and therefore what utilisation is measured on).
 */
const dayPoints = (visits: SfVisit[], day: Date, cfg: PointsConfig) => {
    const onDay = visits.filter((v) => v.visitStartDate && sameDay(new Date(v.visitStartDate), day));
    if (onDay.length === 0) return { recognition: 0, capacity: 0, unrated: 0, modeAssumed: 0 };

    // A visit with no On Site / Remote flag is in neither bucket, so a day of
    // booked work could total zero. Unset means on site — the work is real.
    const modeAssumed = onDay.filter((v) => v.visitCategory == null || v.visitCategory === "").length;

    const occasions = buildOccasions(
        onDay.map(
            (v): OccasionVisit => ({
                accountId: v.accountId,
                accountName: v.accountName,
                date: toISODate(day),
                subActivity: v.subActivity,
                name: v.name,
                visitCategory: v.visitCategory || "On Site",
                industry: v.industry,
                fraRiskLevel: v.fraRiskLevel,
                highRiskFra: v.highRiskFra,
            }),
        ),
        cfg,
    );

    // Remote work never enters an occasion, so it is scored per visit.
    const remote = onDay.filter((v) => v.visitCategory === "Remote").length * cfg.remotePoints;
    const r = (n: number) => Math.round(n * 10) / 10;

    return {
        recognition: r(occasions.reduce((n, o) => n + (o.recognition ?? 0), 0) + remote),
        capacity: r(occasions.reduce((n, o) => n + (o.capacity ?? 0), 0) + remote),
        unrated: occasions.reduce((n, o) => n + o.unratedCount, 0),
        modeAssumed,
    };
};

// Per-visit points. Shows the industry it was rated from, or says plainly why
// no rate applies — the point is that gaps are visible rather than defaulted.
export const PointsBadge = ({ v, compact }: { v: SfVisit; compact?: boolean }) => {
    const { config, showPoints } = usePointsConfig();
    if (!showPoints) return null;
    const score = scoreVisit(
        {
            subActivity: v.subActivity,
            name: v.name,
            visitCategory: v.visitCategory,
            industry: v.industry,
            fraRiskLevel: v.fraRiskLevel,
            highRiskFra: v.highRiskFra,
        },
        config,
        // Same assumption the day total makes, so a chip and the total beneath it
        // can never disagree. This visit is in the diary; only the flag is absent.
        { assumeOnSiteIfUnset: true },
    );

    const rated = score.points != null;
    const reason = score.unrated ? UNRATED_LABEL[score.unrated] : null;
    // Amber means "look at this": either no score, or a score resting on an
    // account with no industry that somebody needs to fix.
    const flagged = !rated || !!score.industryMissing;
    const tip = [
        rated ? `${fmtPoints(score.points)} point${score.points === 1 ? "" : "s"}` : "No points",
        score.rule,
        v.industry ? `industry: ${v.industry}` : "industry NOT SET on this account — please update it in Salesforce",
        score.modeAssumed && "no on-site/remote flag on this visit — counted as on site",
        reason,
        score.mayBeUnderScored &&
            "may be under-scored: a High Risk fire site would score a full day, and no fire-risk field exists yet",
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <Box
            component="span"
            title={tip}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: compact ? "9px" : "10px",
                fontWeight: 800,
                borderRadius: "999px",
                padding: compact ? "1px 5px" : "2px 7px",
                whiteSpace: "nowrap",
                maxWidth: "100%",
                overflow: "hidden",
                color: flagged ? T.amber : T.purpleDeep,
                background: flagged ? T.amberTint : T.purpleTint,
                border: `1px solid ${flagged ? T.amberBorder : "transparent"}`,
            }}
        >
            {rated ? `${fmtPoints(score.points)} pt` : "no pts"}
            {score.mayBeUnderScored && rated ? "?" : ""}
            {!compact && (
                <Box component="span" sx={{ fontWeight: 600, opacity: 0.85, overflow: "hidden", textOverflow: "ellipsis" }}>
                    · {v.industry || "no industry"}
                </Box>
            )}
        </Box>
    );
};

// Exported so the consultant detail page shows the same chip, with the same
// detail, rather than a lookalike that drifts from it.
export const VisitChip = ({ v, onOpen, edited }: { v: SfVisit; onOpen?: (v: SfVisit) => void; edited?: boolean }) => {
    const { bg, fg } = statusStyle(v.status);
    const type = v.subActivity || v.description || "";
    const windowStr = v.visitStartDate ? `${fmtTime(v.visitStartDate)}–${fmtTime(v.visitEndDate)}` : "";
    // Actual on-site time: prefer the server-computed attendance minutes, else derive.
    const derivedMins =
        v.actualStartTime && v.actualEndTime
            ? (new Date(v.actualEndTime).getTime() - new Date(v.actualStartTime).getTime()) / 60000
            : null;
    const dur = fmtDur(v.attendanceMins ?? derivedMins);
    const actualStr = v.actualStartTime
        ? `${fmtTime(v.actualStartTime)}–${fmtTime(v.actualEndTime)}${dur ? ` · ${dur}` : ""}`
        : "";

    return (
        <Box
            onClick={onOpen ? () => onOpen(v) : undefined}
            title={[v.status, windowStr && `booked ${windowStr}`, actualStr && `actual ${actualStr}`, v.visitCategory, onOpen && "click for details"]
                .filter(Boolean)
                .join(" · ")}
            sx={{
                background: bg,
                borderRadius: "6px",
                padding: "5px 8px",
                overflow: "hidden",
                cursor: onOpen ? "pointer" : "default",
                "&:hover": onOpen ? { outline: `1px solid ${fg}` } : undefined,
            }}
        >
            <Typography sx={{ fontSize: "11px", fontWeight: 800, color: fg, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {edited ? "✎ " : ""}{v.accountName || "Unnamed"}
            </Typography>
            {type && (
                <Typography sx={{ fontSize: "10px", fontWeight: 600, color: fg, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {type}
                </Typography>
            )}
            {windowStr && (
                <Typography sx={{ fontSize: "9.5px", color: fg, opacity: 0.85, whiteSpace: "nowrap" }}>
                    🕘 {windowStr}
                </Typography>
            )}
            {actualStr && (
                <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: fg, whiteSpace: "nowrap" }}>
                    ✓ {actualStr}
                </Typography>
            )}
            <Box sx={{ mt: "3px" }}>
                <PointsBadge v={v} />
            </Box>
        </Box>
    );
};

/**
 * Day timeline (08:00-18:00). Positions each visit's booked window on a 10-hour
 * track so morning vs afternoon, and double-stacked days, read at a glance.
 */
const DAY_START_MIN = 8 * 60;
const DAY_END_MIN = 18 * 60;
const DAY_SPAN = DAY_END_MIN - DAY_START_MIN;

const minsOfDay = (iso?: string) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d.getHours() * 60 + d.getMinutes();
};

const DayTimeline = ({ visits }: { visits: SfVisit[] }) => {
    const blocks = visits
        .map((v) => {
            const s = minsOfDay(v.visitStartDate);
            const e = minsOfDay(v.visitEndDate);
            if (s == null) return null;
            const end = e == null || e <= s ? s + 60 : e;
            const left = Math.max(0, Math.min(100, ((s - DAY_START_MIN) / DAY_SPAN) * 100));
            const right = Math.max(0, Math.min(100, ((end - DAY_START_MIN) / DAY_SPAN) * 100));
            const width = Math.max(3, right - left);
            const { fg } = statusStyle(v.status);
            return { v, left, width, fg };
        })
        .filter(Boolean) as Array<{ v: SfVisit; left: number; width: number; fg: string }>;

    if (!blocks.length) return null;

    return (
        <Box sx={{ position: "relative", height: "12px", borderRadius: "4px", background: T.borderSoft, overflow: "hidden", mb: "2px" }}>
            {/* midday tick (13:00) */}
            <Box sx={{ position: "absolute", top: 0, bottom: 0, left: "50%", width: "1px", background: "#CBD1E2" }} />
            {blocks.map((b, i) => (
                <Box
                    key={b.v.id ?? i}
                    title={`${b.v.accountName ?? ""} · ${fmtTime(b.v.visitStartDate)}–${fmtTime(b.v.visitEndDate)}`}
                    sx={{
                        position: "absolute",
                        top: "2px",
                        bottom: "2px",
                        left: `${b.left}%`,
                        width: `${b.width}%`,
                        background: b.fg,
                        borderRadius: "3px",
                        opacity: 0.9,
                    }}
                />
            ))}
        </Box>
    );
};

/**
 * How the day was actually spent. With per-visit actuals, replays the day in ORDER
 * on the same 08:00-18:00 axis as the strip above — travel, on site, write-up;
 * otherwise aggregate proportions of an 8-hour day. Green on site, amber travel,
 * purple write-up.
 */
const WORKING_DAY_MINS = 480;

interface Seg {
    from: number; // minutes-of-day
    to: number;
    color: string;
    label: string;
}

// Chronological segments from the visits' actual + travel times; one combined
// write-up block is appended after the day's last recorded moment.
const daySegments = (visits: SfVisit[]): Seg[] => {
    const segs: Seg[] = [];
    let cursor = 0;
    let writeUp = 0;
    visits.forEach((v) => {
        const aS = minsOfDay(v.actualStartTime);
        const aE = minsOfDay(v.actualEndTime);
        if (aS == null || aE == null || aE <= aS) return;
        const tS = minsOfDay(v.travelStartTime);
        const tE = minsOfDay(v.travelEndTime);
        if (tS != null && tS < aS) segs.push({ from: tS, to: aS, color: T.amber, label: "travel" });
        segs.push({ from: aS, to: aE, color: T.green, label: "on site" });
        if (tE != null && tE > aE) segs.push({ from: aE, to: tE, color: T.amber, label: "travel" });
        cursor = Math.max(cursor, tE ?? aE);
        writeUp += Number(v.writeUpTimeMinutes ?? 0);
    });
    if (segs.length && writeUp > 0) segs.push({ from: cursor, to: cursor + writeUp, color: T.purple, label: "write-up" });
    return segs;
};

const DayBar = ({
    cell,
    plannedMins,
    visits,
}: {
    cell?: { actSiteMins?: number | null; travelTimeMins?: number; writeUpMins?: number | null };
    plannedMins: number;
    visits: SfVisit[];
}) => {
    const segs = daySegments(visits);

    if (segs.length > 0) {
        // Chronological view on the 08:00–18:00 axis.
        const pos = (m: number) => Math.max(0, Math.min(100, ((m - DAY_START_MIN) / DAY_SPAN) * 100));
        const totals = segs.reduce(
            (t, s) => ({ ...t, [s.label]: (t as any)[s.label] + (s.to - s.from) }),
            { travel: 0, "on site": 0, "write-up": 0 } as Record<string, number>,
        );
        const took = totals.travel + totals["on site"] + totals["write-up"];
        return (
            <Box sx={{ mt: "3px" }}>
                <Box sx={{ position: "relative", height: "8px", borderRadius: "4px", background: T.borderSoft, overflow: "hidden" }}>
                    <Box sx={{ position: "absolute", top: 0, bottom: 0, left: "50%", width: "1px", background: "#CBD1E2" }} />
                    {segs.map((s, i) => (
                        <Box
                            key={i}
                            title={
                                // Salesforce records a quantity of write-up minutes with no
                                // timestamps, so showing a clock range would invent one.
                                s.label === "write-up"
                                    ? `write-up ${fmtDur(s.to - s.from)} · time of day not recorded`
                                    : `${s.label} ${fmtDur(s.to - s.from)} (${String(Math.floor(s.from / 60)).padStart(2, "0")}:${String(s.from % 60).padStart(2, "0")}–${String(Math.floor(s.to / 60)).padStart(2, "0")}:${String(s.to % 60).padStart(2, "0")})`
                            }
                            sx={{
                                position: "absolute",
                                top: "1px",
                                bottom: "1px",
                                // Striped = duration only, position not meaningful.
                                ...(s.label === "write-up"
                                    ? { backgroundImage: `repeating-linear-gradient(45deg, ${T.purple} 0 3px, transparent 3px 6px)` }
                                    : null),
                                left: `${pos(s.from)}%`,
                                width: `${Math.max(1.5, pos(s.to) - pos(s.from))}%`,
                                background: s.color,
                                borderRadius: "2px",
                            }}
                        />
                    ))}
                </Box>
                <Typography sx={{ fontSize: "9px", color: T.muted, mt: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    took {fmtDur(took)} (🚗 {fmtDur(totals.travel)} · 🏭 {fmtDur(totals["on site"])}
                    {totals["write-up"] > 0 ? ` · ✍ ${fmtDur(totals["write-up"])}` : ""})
                    {plannedMins > 0 ? ` · planned ${fmtDur(plannedMins)}` : ""}
                </Typography>
            </Box>
        );
    }

    // Aggregate fallback (no per-visit actuals): proportions of an 8-hour day.
    const site = Number(cell?.actSiteMins ?? 0);
    const travel = cell?.travelTimeMins ?? 0;
    const writeUp = Number(cell?.writeUpMins ?? 0);
    const worked = site + travel + writeUp;
    if (worked <= 0 && plannedMins <= 0) return null;

    const pct = (n: number) => Math.max(0, Math.min(100, (n / WORKING_DAY_MINS) * 100));
    const tip = [
        site > 0 ? `site ${fmtDur(site)}` : "",
        travel > 0 ? `travel ${fmtDur(travel)}` : "",
        writeUp > 0 ? `write-up ${fmtDur(writeUp)}` : "",
        plannedMins > 0 ? `planned ${fmtDur(plannedMins)}` : "",
    ].filter(Boolean).join(" · ");

    return (
        <Box sx={{ mt: "5px" }} title={tip}>
            <Box sx={{ position: "relative", height: "6px", borderRadius: "3px", background: T.borderSoft, overflow: "hidden" }}>
                <Box sx={{ position: "absolute", inset: 0, display: "flex" }}>
                    <Box sx={{ width: `${pct(site)}%`, background: T.green }} />
                    <Box sx={{ width: `${pct(travel)}%`, background: T.amber }} />
                    <Box sx={{ width: `${pct(writeUp)}%`, background: T.purple }} />
                </Box>
                {plannedMins > 0 && (
                    <Box sx={{ position: "absolute", top: 0, bottom: 0, width: "2px", background: T.ink2, left: `calc(${pct(plannedMins)}% - 1px)` }} />
                )}
            </Box>
            <Typography sx={{ fontSize: "9px", color: T.muted, mt: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {worked > 0 ? `took ${fmtDur(worked)}` : ""}
                {worked > 0 && plannedMins > 0 ? " · " : ""}
                {plannedMins > 0 ? `planned ${fmtDur(plannedMins)}` : ""}
            </Typography>
        </Box>
    );
};

/** The day's points, sat bottom-right of the cell. */
const DayTotal = ({ visits, day }: { visits: SfVisit[]; day: Date }) => {
    const { config, showPoints } = usePointsConfig();
    const pts = dayPoints(visits, day, config);
    if (visits.length === 0 || !showPoints) return null;

    return (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "1px" }}>
            <Typography
                title={[
                    `${pts.recognition} recognition points this day`,
                    `${pts.capacity} of capacity used`,
                    visits.length > 1 ? "visits at one account count as a single occasion — highest points plus a supplement, not the sum" : null,
                    pts.unrated ? `${pts.unrated} visit(s) unrated` : null,
                    pts.modeAssumed ? `${pts.modeAssumed} visit(s) have no on-site/remote flag and are counted as on site` : null,
                ]
                    .filter(Boolean)
                    .join(" · ")}
                sx={{ fontSize: "10px", fontWeight: 800, color: T.purpleDeep, fontVariantNumeric: "tabular-nums" }}
            >
                {fmtPoints(pts.recognition)} pts
                {!!pts.unrated && (
                    <Box component="span" sx={{ color: T.amber, ml: "3px" }}>
                        ⌂
                    </Box>
                )}
            </Typography>
        </Box>
    );
};

/**
 * Exported for the consultant detail page's week view.
 *
 * Branch order matters: VISITS are handled before the absence comment, so a day
 * carrying both a booking and a diary exception still shows the booking.
 */
export const DayCell = ({
    c,
    day,
    visits,
    loading,
    detailFailed,
    onVisitClick,
    editedIds,
    overrideComment,
    onEditDay,
    onPlanDay,
    highlightGaps,
}: {
    c: VisitConsultantT;
    day: Date;
    visits: SfVisit[];
    loading: boolean;
    detailFailed: boolean;
    onVisitClick?: (v: SfVisit) => void;
    editedIds?: Set<string>;
    overrideComment?: string | null; // local Admin-day edit (null = removed)
    onEditDay?: (existingAdmin: boolean) => void;
    onPlanDay?: (existing: SfVisit[]) => void; // open the day planner
    highlightGaps?: boolean; // "Open gaps" filter is on — make empty days shout
}) => {
    const { showPoints } = usePointsConfig();
    const cell = dayCellsFor(c, day);
    // Effective non-visit-day comment: local override (incl. removal) beats source.
    const comment = overrideComment !== undefined ? overrideComment : cell?.comment ?? null;
    const dayVisits = visits
        .filter((v) => v.visitStartDate && sameDay(new Date(v.visitStartDate), day))
        .sort((a, b) => (a.visitStartDate || "").localeCompare(b.visitStartDate || ""));

    // Planned (booked-window) minutes for the day, from the visit details.
    const plannedMins = dayVisits.reduce((n, v) => {
        if (!v.visitStartDate || !v.visitEndDate) return n;
        const mins = (new Date(v.visitEndDate).getTime() - new Date(v.visitStartDate).getTime()) / 60000;
        return n + (isFinite(mins) && mins > 0 && mins < 24 * 60 ? mins : 0);
    }, 0);

    let body;
    if (dayVisits.length > 0) {
        body = (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", width: "100%" }}>
                <DayTimeline visits={dayVisits} />
                {dayVisits.map((v, i) => (
                    <VisitChip key={v.id ?? i} v={v} onOpen={onVisitClick} edited={editedIds?.has(v.id)} />
                ))}
                {showPoints && !comment && !isPastDay(day) && roomForSecond(dayVisits) && (
                    <Box
                        onClick={onPlanDay ? () => onPlanDay(dayVisits) : undefined}
                        title={`Single visit booked for ≤5h, so flagged as a pairing candidate — travel, distance and the visit pool are not assessed yet.${
                            onPlanDay ? " Click to plan the day." : ""
                        }`}
                        sx={{
                            border: `1px dashed ${T.amber}`,
                            color: T.amber,
                            borderRadius: "6px",
                            padding: "3px 8px",
                            fontSize: "10px",
                            fontWeight: 800,
                            textAlign: "center",
                            cursor: onPlanDay ? "pointer" : "default",
                            "&:hover": onPlanDay ? { background: T.amberTint } : undefined,
                        }}
                    >
                        ⚡ pairing candidate{onPlanDay ? " — plan it" : ""}
                    </Box>
                )}
                <DayBar cell={cell} plannedMins={plannedMins} visits={dayVisits} />
                <DayTotal visits={dayVisits} day={day} />
            </Box>
        );
    } else if (loading && (cell?.bookingCount ?? 0) > 0) {
        // detail still loading — show the count from the weekly summary meanwhile
        body = (
            <Box sx={{ background: T.purpleTint, color: T.purpleDeep, borderRadius: "6px", padding: "6px 8px", fontSize: "12px", fontWeight: 700, opacity: 0.6, width: "100%" }}>
                {cell!.bookingCount} visit{cell!.bookingCount > 1 ? "s" : ""}…
            </Box>
        );
    } else if ((cell?.bookingCount ?? 0) > 0) {
        // summary says there are bookings but the detail fetch had nothing (or failed):
        // still show the count AND how the day's recorded time was spent.
        body = (
            <Box sx={{ width: "100%" }}>
                <Box sx={{ background: T.purpleTint, color: T.purpleDeep, borderRadius: "6px", padding: "6px 8px", fontSize: "12px", fontWeight: 700 }}>
                    {cell!.bookingCount} visit{cell!.bookingCount > 1 ? "s" : ""}
                    {detailFailed ? " ⚠" : ""}
                </Box>
                <DayBar cell={cell} plannedMins={0} visits={[]} />
            </Box>
        );
    } else if (comment) {
        const editable = comment === "Admin" && !!onEditDay;
        body = (
            <Box
                onClick={editable ? () => onEditDay!(true) : undefined}
                title={editable ? "Admin day — click to move or remove" : `${comment} — synced from Youmanage/HR, managed there`}
                sx={{
                    background: T.amberTint,
                    color: T.amber,
                    borderRadius: "6px",
                    padding: "6px 8px",
                    fontSize: "11.5px",
                    fontWeight: 600,
                    width: "100%",
                    cursor: editable ? "pointer" : "default",
                    "&:hover": editable ? { outline: `1px solid ${T.amber}` } : undefined,
                }}
            >
                {comment}
            </Box>
        );
    } else if (cell && ((cell.actSiteMins ?? 0) > 0 || (cell.travelTimeMins ?? 0) > 0 || (Number(cell.writeUpMins) || 0) > 0)) {
        // no booking count, but time was logged that day
        body = (
            <Box sx={{ width: "100%" }}>
                <DayBar cell={cell} plannedMins={0} visits={[]} />
            </Box>
        );
    } else if (onPlanDay && !isPastDay(day)) {
        // empty day still ahead of us — open the day planner; a past empty day is just history
        body = (
            <Box
                onClick={() => onPlanDay([])}
                title="Plan this day — book a pool visit, see engine suggestions, or add an admin day"
                sx={{
                    border: `1.5px dashed ${highlightGaps ? T.red : T.borderSoft}`,
                    borderRadius: "6px",
                    color: highlightGaps ? T.red : "#C7CDDD",
                    background: highlightGaps ? "#FBF1EF" : "transparent",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: 700,
                    padding: "4px 0",
                    width: "100%",
                    cursor: "pointer",
                    "&:hover": { color: T.purple, borderColor: T.purple, background: T.purpleTint },
                }}
            >
                +
            </Box>
        );
    } else if (cell && isPastDay(day)) {
        // The summary covered this day and it has nothing on it — a working day that went unrecorded.
        body = (
            <Box
                title="Nothing recorded this day — no visit, no leave or admin, no time logged"
                sx={{
                    width: "100%",
                    textAlign: "center",
                    color: T.amber,
                    fontSize: "10.5px",
                    fontWeight: 700,
                    border: `1px dashed ${T.amberBorder}`,
                    borderRadius: "6px",
                    padding: "4px 0",
                }}
            >
                ⚠ nothing recorded
            </Box>
        );
    } else {
        body = <Box sx={{ color: "#C7CDDD", fontSize: "13px", textAlign: "center", width: "100%" }}>·</Box>;
    }

    return (
        <Box sx={{ minHeight: "64px", borderLeft: `1px solid ${T.borderSoft}`, padding: "7px", display: "flex", alignItems: "center" }}>
            {body}
        </Box>
    );
};

const ConsultantRow = ({
    c,
    weekDates,
    onOpen,
    overrides,
    onVisitClick,
    dayOverrides,
    onEditDay,
    onPlanDay,
    highlightGaps,
}: {
    c: VisitConsultantT;
    weekDates: Date[];
    onOpen: () => void;
    overrides?: Record<string, Partial<SfVisit>>;
    onVisitClick?: (v: SfVisit) => void;
    dayOverrides?: Record<string, string | null>; // `${email}|${dateISO}` → comment (null = removed)
    onEditDay?: (day: Date, existingAdmin: boolean) => void;
    onPlanDay?: (day: Date, existing: SfVisit[]) => void;
    highlightGaps?: boolean;
}) => {
    const { config: pointsConfig, showPoints } = usePointsConfig();
    // Pull this consultant's real visits for the visible week (Sat exclusive) so
    // cells can show client, type, booked window and actual times.
    const from = toISODate(weekDates[0]);
    const to = toISODate(addDays(weekDates[4], 1));
    const query = useConsultantVisits(from, to, c.email, true);
    const rawVisits = query.data;
    // Local prototype edits (visit modal) layered over the source data.
    const visits = (rawVisits || []).map((v) => (overrides?.[v.id] ? { ...v, ...overrides[v.id] } : v));
    const isLoading = query.isLoading;
    const isError = query.isError;

    // Week totals column — same metrics as month view.
    const weekVisitList = (visits || []).filter(
        (v) => v.visitStartDate && weekDates.some((d) => sameDay(new Date(v.visitStartDate as string), d)),
    );
    const effComment = (d: Date) => {
        const k = `${c.email}|${toISODate(d)}`;
        return dayOverrides && k in dayOverrides ? dayOverrides[k] : dayCellsFor(c, d)?.comment ?? null;
    };
    const missedDoubles = weekDates.filter((d) => {
        if (isPastDay(d) || effComment(d)) return false;
        const dv = weekVisitList.filter((v) => sameDay(new Date(v.visitStartDate as string), d));
        return roomForSecond(dv);
    }).length;

    // Week points sum the per-day figures, so the total always agrees with the
    // cells above it. Utilisation is capacity against a week's field-day points.
    const weekPts = weekDates.reduce(
        (t, d) => {
            const p = dayPoints(weekVisitList, d, pointsConfig);
            return { recognition: t.recognition + p.recognition, capacity: t.capacity + p.capacity };
        },
        { recognition: 0, capacity: 0 },
    );
    const weekAvailable = availablePoints(pointsConfig.fieldDaysPerYear / 52, pointsConfig);
    const ptsUtil = weekAvailable > 0 ? Math.round((weekPts.capacity / weekAvailable) * 100) : null;
    const exceptionFor = (d: Date): DayException => {
        const k = `${c.email}|${toISODate(d)}`;
        if (dayOverrides && k in dayOverrides) {
            const o = dayOverrides[k];
            return o ? { comment: o, status: o === "Admin" ? "A" : null } : null;
        }
        return dayExceptionFor(c, d);
    };
    const util = daysUtilisation(weekDates, weekVisitList, exceptionFor);

    // Week totals from the summary's per-day minutes (the 5 visible days only).
    const dayCells = weekDates.map((d) => dayCellsFor(c, d));
    const wkSite = dayCells.reduce((n, cell) => n + Number(cell?.actSiteMins ?? 0), 0);
    const wkTravel = dayCells.reduce((n, cell) => n + (cell?.travelTimeMins ?? 0), 0);
    const wkWriteUp = dayCells.reduce((n, cell) => n + Number(cell?.writeUpMins ?? 0), 0);
    const wkParts = [
        wkSite > 0 ? `site ${fmtDur(wkSite)}` : "",
        wkTravel > 0 ? `travel ${fmtDur(wkTravel)}` : "",
        wkWriteUp > 0 ? `write-up ${fmtDur(wkWriteUp)}` : "",
    ].filter(Boolean);

    return (
        <Box sx={{ display: "grid", gridTemplateColumns: gridFor(showPoints), borderTop: `1px solid ${T.borderSoft}` }}>
            <Box onClick={onOpen} sx={{ padding: "10px", display: "flex", flexDirection: "column", justifyContent: "center", gap: "3px", cursor: "pointer", minWidth: 0, "&:hover": { background: T.rowTint } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "9px", minWidth: 0 }}>
                    <Box sx={{ width: "30px", height: "30px", borderRadius: "50%", background: T.ink, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: 700, flexShrink: 0 }}>
                        {initials(c.name)}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {c.name}
                        </Typography>
                        <Box
                            title={`Points utilisation this week: ${Math.round(weekPts.capacity * 10) / 10} of ${weekAvailable} capacity points (${(pointsConfig.fieldDaysPerYear / 52).toFixed(1)} field days at ${fmtPoints(pointsConfig.consultancyFullPoints)} a day)`}
                            sx={{ display: showPoints ? "flex" : "none", alignItems: "center", gap: "6px", mt: "2px" }}
                        >
                            <Box sx={{ width: "56px" }}><Meter pct={ptsUtil ?? 0} color={utilColour(ptsUtil)} /></Box>
                            <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: T.purpleDeep, fontVariantNumeric: "tabular-nums" }}>
                                {isLoading || ptsUtil == null ? "—" : `${ptsUtil}% pts`}
                            </Typography>
                        </Box>
                        <Typography
                            title="Days utilisation this week: days used (a visit, admin or training) against working days, with holiday and sickness taken out. Four visits on one day is one day; an admin day is one day."
                            sx={{ fontSize: "9.5px", color: T.muted2, mt: "1px", fontVariantNumeric: "tabular-nums" }}
                        >
                            {isLoading || util == null ? "— days" : `${util}% days`}
                        </Typography>
                    </Box>
                </Box>
                {wkParts.length > 0 && (
                    <Typography sx={{ fontSize: "9.5px", color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        ⏱ wk: {wkParts.join(" · ")}
                    </Typography>
                )}
            </Box>
            {weekDates.map((d, i) => {
                const k = `${c.email}|${toISODate(d)}`;
                return (
                    <DayCell
                        key={i}
                        c={c}
                        day={d}
                        visits={visits}
                        loading={isLoading}
                        detailFailed={isError}
                        onVisitClick={onVisitClick}
                        editedIds={overrides ? new Set(Object.keys(overrides)) : undefined}
                        overrideComment={dayOverrides && k in dayOverrides ? dayOverrides[k] : undefined}
                        onEditDay={onEditDay ? (existingAdmin) => onEditDay(d, existingAdmin) : undefined}
                        onPlanDay={onPlanDay ? (existing) => onPlanDay(d, existing) : undefined}
                        highlightGaps={highlightGaps}
                    />
                );
            })}
            {showPoints && (
            <Box sx={{ padding: "8px 10px 8px 4px", textAlign: "right", borderLeft: `1px solid ${T.borderSoft}` }}>
                {showPoints && (
                    <Typography
                        title={`${Math.round(weekPts.recognition * 10) / 10} recognition points this week`}
                        sx={{ fontSize: "11px", fontWeight: 800, color: T.purpleDeep, fontVariantNumeric: "tabular-nums" }}
                    >
                        {isLoading ? "…" : `${fmtPoints(Math.round(weekPts.recognition * 10) / 10)} pts`}
                    </Typography>
                )}
                {missedDoubles > 0 && (
                    <Typography title="Single-visit days booked for ≤5h — pairing candidates, not confirmed misses" sx={{ fontSize: "10px", fontWeight: 800, color: T.amber }}>
                        ⚡ {missedDoubles} pairing
                    </Typography>
                )}
            </Box>
            )}
        </Box>
    );
};

/**
 * Month heatmap. Exported for the consultant detail page's month view, so a cell
 * means the same thing — colours, points, click-through — on both boards.
 */
export const MonthCell = ({
    c,
    day,
    visits,
    onJump,
}: {
    c: VisitConsultantT;
    day: Date;
    visits: SfVisit[];
    onJump?: (d: Date) => void;
}) => {
    const cell = dayCellsFor(c, day);
    const dayVisits = visits.filter((v) => v.visitStartDate && sameDay(new Date(v.visitStartDate as string), day));
    let bg: string = "transparent";
    let fg: string = T.muted2;
    let text = "";
    let tip = day.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });

    const { config, showPoints } = usePointsConfig();
    const pairingCandidate = !cell?.comment && !isPastDay(day) && roomForSecond(dayVisits);
    if (cell?.comment) {
        // dayStatus is the first letter of the exception TYPE (H/T/A/S/P). The
        // comment is free text, so its first letter mislabelled Holiday as Admin.
        bg = T.amberTint; fg = T.amber; text = cell.dayStatus || cell.comment[0];
        tip += ` · ${cell.comment}`;
    } else if (dayVisits.length > 0) {
        // "Closed" means closed off without delivery — it must not read as completed.
        const allDone = dayVisits.every((v) => (v.status || "").toLowerCase() === "completed");
        const allClosed = dayVisits.every((v) => (v.status || "").toLowerCase() === "closed");
        const anyProposal = dayVisits.some((v) => (v.status || "").toLowerCase() === "automation proposal");
        bg = allDone ? T.greenTint : allClosed ? T.borderSoft : anyProposal ? T.amberTint : T.purpleTint;
        fg = allDone ? T.green : allClosed ? T.muted : anyProposal ? T.amber : T.purpleDeep;
        // Points are this cell's ONLY text, so with them hidden it falls back to the
        // visit count rather than rendering a coloured but blank square.
        const pts = dayPoints(dayVisits, day, config);
        const cellValue = showPoints ? fmtPoints(pts.recognition) : String(dayVisits.length);
        text = pairingCandidate && showPoints ? `${cellValue}⚡` : cellValue;
        tip += showPoints
            ? ` · ${fmtPoints(pts.recognition)} points from ${dayVisits.length} visit(s)`
            : ` · ${dayVisits.length} visit(s)`;
        tip += dayVisits.map((v) => ` · ${v.accountName} ${fmtTime(v.visitStartDate)}`).join("");
        if (pairingCandidate && showPoints) tip += " · booked ≤5h, so flagged as a pairing candidate (travel not assessed)";
    } else if ((cell?.bookingCount ?? 0) > 0) {
        bg = T.purpleTint; fg = T.purpleDeep; text = String(cell!.bookingCount);
        tip += ` · ${cell!.bookingCount} visit(s)`;
    } else if (cell && isPastDay(day)) {
        fg = T.amber; text = "!";
        tip += " · nothing recorded — no visit, leave or admin";
    }

    return (
        <Box
            onClick={() => onJump?.(day)}
            title={`${tip}${onJump ? " · click for week view" : ""}`}
            sx={{
                height: "26px",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10.5px",
                fontWeight: 700,
                background: bg,
                color: fg,
                cursor: onJump ? "pointer" : "default",
                border: pairingCandidate && showPoints ? `1px dashed ${T.amber}` : text ? "none" : `1px dashed ${T.borderSoft}`,
                "&:hover": onJump ? { outline: `1px solid ${T.purple}` } : undefined,
            }}
        >
            {text}
        </Box>
    );
};

const MonthRow = ({
    c,
    dates,
    gridTemplate,
    onOpen,
    onJump,
}: {
    c: VisitConsultantT;
    dates: Date[];
    gridTemplate: string;
    onOpen: () => void;
    onJump?: (d: Date) => void;
}) => {
    const from = toISODate(dates[0]);
    const to = toISODate(addDays(dates[dates.length - 1], 1));
    const query = useConsultantVisits(from, to, c.email, true);
    const visits = query.data ?? [];
    const { config: pointsConfig, showPoints } = usePointsConfig();

    // Only dates the weekly summary covers can be checked for leave or admin, so
    // pairing candidates are limited to those. Points are not — they come from visits.
    const covered = dates.filter((d) => dayCellsFor(c, d) != null);
    // Month points, from the same per-day helper the cells and the week use, so
    // the three can never disagree.
    const monthPts = dates.reduce(
        (t, d) => {
            const dp = dayPoints(visits, d, pointsConfig);
            return { recognition: t.recognition + dp.recognition, capacity: t.capacity + dp.capacity };
        },
        { recognition: 0, capacity: 0 },
    );
    const monthAvailable = availablePoints(fieldDaysPerMonth(pointsConfig), pointsConfig);
    const ptsUtil = monthAvailable > 0 ? Math.round((monthPts.capacity / monthAvailable) * 100) : null;
    const util = daysUtilisation(dates, visits, (d) => dayExceptionFor(c, d));
    const missedDoubles = covered.filter((d) => {
        if (isPastDay(d) || dayCellsFor(c, d)?.comment) return false;
        const dv = visits.filter((v) => v.visitStartDate && sameDay(new Date(v.visitStartDate as string), d));
        return roomForSecond(dv);
    }).length;

    return (
        <Box sx={{ display: "grid", gridTemplateColumns: gridTemplate, alignItems: "center", gap: "3px", borderTop: `1px solid ${T.borderSoft}`, padding: "5px 0" }}>
            <Box onClick={onOpen} sx={{ padding: "4px 10px", display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", minWidth: 0, "&:hover": { background: T.rowTint } }}>
                <Box sx={{ width: "26px", height: "26px", borderRadius: "50%", background: T.ink, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700, flexShrink: 0 }}>
                    {initials(c.name)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {c.name}
                    </Typography>
                    <Typography
                        title={
                            showPoints
                                ? `Points utilisation this month: ${Math.round(monthPts.capacity * 10) / 10} of ${monthAvailable} capacity points`
                                : "Days utilisation this month: days used (a visit, admin or training) against working days, with holiday and sickness taken out."
                        }
                        sx={{ fontSize: "10px", fontWeight: 700, color: T.purpleDeep, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
                    >
                        {showPoints ? (ptsUtil == null ? "—" : `${ptsUtil}% pts`) : null}
                        <Box
                            component="span"
                            title="Days utilisation this month: days used (a visit, admin or training) against working days, with holiday and sickness taken out. Exceptions outside the summary's window cannot be seen, so those days count as available."
                            sx={{
                                color: showPoints ? T.muted2 : T.purpleDeep,
                                fontWeight: showPoints ? 600 : 700,
                                ml: showPoints ? "6px" : 0,
                            }}
                        >
                            {util == null ? "— days" : `${util}% days`}
                        </Box>
                    </Typography>
                </Box>
            </Box>
            {dates.map((d, i) => (
                <MonthCell key={i} c={c} day={d} visits={visits} onJump={onJump} />
            ))}
            {showPoints && (
            <Box sx={{ paddingRight: "10px", textAlign: "right" }}>
                {showPoints && (
                    <Typography
                        title={`${Math.round(monthPts.recognition * 10) / 10} recognition points this month`}
                        sx={{ fontSize: "11px", fontWeight: 800, color: T.purpleDeep, fontVariantNumeric: "tabular-nums" }}
                    >
                        {fmtPoints(Math.round(monthPts.recognition * 10) / 10)} pts
                    </Typography>
                )}
                {missedDoubles > 0 && (
                    <Typography title="Single-visit days booked for ≤5h — pairing candidates, not confirmed misses" sx={{ fontSize: "10px", fontWeight: 800, color: T.amber }}>
                        ⚡ {missedDoubles} pairing
                    </Typography>
                )}
            </Box>
            )}
        </Box>
    );
};

const ScheduleScreen = ({
    data,
    isLoading,
    isError,
    team = "AllTeams",
    consultant = "AllConsultants",
    dates,
    mode = "week",
    initialGapsOnly,
    onJumpToDate,
    onOpen,
    onGoPool,
}: {
    data: WeeklyVisitSummary | null | undefined;
    isLoading: boolean;
    isError: boolean;
    team?: string;
    /** A consultant's email, or "AllConsultants". */
    consultant?: string;
    dates: Date[];
    mode?: "week" | "month";
    /** Set when Overview sent the user here to look at gaps. */
    initialGapsOnly?: boolean;
    onJumpToDate?: (d: Date) => void;
    onOpen: (teamName: string, c: VisitConsultantT) => void;
    /** Day planner → the Visit pool page, pinned to that consultant. */
    onGoPool?: (email: string, name: string) => void;
}) => {
    const { showPoints } = usePointsConfig();
    const weekDates = dates;
    // Visit modal + local prototype edits (kept here so edits survive week stepping).
    const [selectedVisit, setSelectedVisit] = useState<SfVisit | null>(null);
    const [overrides, setOverrides] = useState<Record<string, Partial<SfVisit>>>({});
    const applyEdit = (id: string, patch: Partial<SfVisit>) =>
        setOverrides((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    // "Open gaps" tile toggle — filters rows to consultants with gaps.
    const [gapsOnly, setGapsOnly] = useState(!!initialGapsOnly);
    // Switched-off consultants are out of the board and out of every total.
    const roster = useActiveConsultants();

    // The same visits every row fetches, under the same keys, so this is a cache
    // read — the tile just needs them all in one place to average.
    const periodFrom = dates.length ? toISODate(dates[0]) : "";
    const periodTo = dates.length ? toISODate(addDays(dates[dates.length - 1], 1)) : "";
    const periodQuery = useVisitsByConsultant(periodFrom, periodTo, !!periodFrom);

    // Day planner: local pool bookings (applied to this board only).
    const [plannerTarget, setPlannerTarget] = useState<PlannerTarget | null>(null);
    // Admin-day modal + local day edits (`${email}|${dateISO}` → "Admin" | null-removed).
    const [dayTarget, setDayTarget] = useState<DayEditTarget | null>(null);
    const [dayOverrides, setDayOverrides] = useState<Record<string, string | null>>({});
    const applyDayEdit = (email: string, originalISO: string, newISO: string | null, wasExisting: boolean) =>
        setDayOverrides((prev) => {
            const next = { ...prev };
            if (newISO === null) {
                next[`${email}|${originalISO}`] = null; // removed
                return next;
            }
            if (wasExisting && newISO !== originalISO) next[`${email}|${originalISO}`] = null; // moved away
            next[`${email}|${newISO}`] = "Admin";
            return next;
        });
    if (isLoading)
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: "48px" }}>
                <CircularProgress size={26} />
            </Box>
        );
    if (isError) return <Box sx={{ p: "28px", color: T.muted }}>Couldn't load the schedule.</Box>;
    if (!data) return <HsAdminGate />;

    // Rows come from the consultant ROSTER, not from whoever has a visit — the
    // weekly summary holds only consultants with bookings, hiding empty diaries.
    const summaryByEmail = new Map<string, VisitConsultantT>();
    (data.regions || []).forEach((r) =>
        (r.consultants || []).forEach((c) => {
            if (c.email) summaryByEmail.set(c.email.toLowerCase(), c);
        }),
    );

    const rosterRows = roster.active
        .filter((rc) => team === "AllTeams" || (rc.team || "Unknown") === team)
        .filter(
            (rc) =>
                consultant === "AllConsultants" ||
                (rc.email || "").toLowerCase() === consultant.toLowerCase(),
        )
        .map((rc) => ({
            teamName: rc.team || "No team",
            consultant:
                summaryByEmail.get((rc.email || "").toLowerCase()) ??
                ({ name: rc.name, email: rc.email, data: [] } as unknown as VisitConsultantT),
        }));

    // Grouped by TEAM. Consultants with no team group under "No team" rather than
    // disappearing.
    const groups: ConsultantGroup[] = roster.active.length
        ? [...new Set(rosterRows.map((r) => r.teamName))].sort().map((name) => ({
              name,
              consultants: rosterRows.filter((r) => r.teamName === name).map((r) => r.consultant),
          }))
        : // Roster still loading (or failed) — fall back to the summary's own
          // people as one flat group, so the board degrades rather than blanking.
          [{ name: "All consultants", consultants: (data.regions || []).flatMap((r) => r.consultants || []) }];

    const showTeamHeaders = team === "AllTeams" && groups.length > 1;

    // ── Top metrics for the visible period/team (live: reflects local board
    //    edits — booked pool visits and added/removed admin days) ─────────────
    const visibleConsultants = groups.flatMap((g) => g.consultants);
    let gaps = 0, singles = 0, visitTotal = 0, offDays = 0, uncovered = 0;
    const gapsByEmail: Record<string, number> = {};
    visibleConsultants.forEach((c) => {
        dates.forEach((d) => {
            const cell = dayCellsFor(c, d);
            const k = `${c.email}|${toISODate(d)}`;
            const overridden = dayOverrides ? k in dayOverrides : false;

            // A missing summary row means the API was never asked about this date,
            // not that the day is free — the 28-day window cannot span a month.
            if (!cell && !overridden) {
                uncovered++;
                return;
            }

            const comment = overridden ? dayOverrides[k] : cell?.comment ?? null;
            const bookings = cell?.bookingCount ?? 0;
            visitTotal += bookings;
            if (comment) offDays++;
            // A past empty day is history, not a gap; a past single visit cannot be paired.
            else if (isPastDay(d)) return;
            else if (bookings === 0) {
                gaps++;
                gapsByEmail[c.email] = (gapsByEmail[c.email] ?? 0) + 1;
            } else if (bookings === 1) singles++;
        });
    });
    const visitsByEmail = periodQuery.byEmail;
    const utils = visibleConsultants
        .map((c) => {
            const v = visitsByEmail.get((c.email || "").toLowerCase());
            if (!v) return null;
            const exceptionFor = (d: Date): DayException => {
                const k = `${c.email}|${toISODate(d)}`;
                if (k in dayOverrides) {
                    const o = dayOverrides[k];
                    return o ? { comment: o, status: o === "Admin" ? "A" : null } : null;
                }
                return dayExceptionFor(c, d);
            };
            return daysUtilisation(dates, v, exceptionFor);
        })
        .filter((u): u is number => u != null);
    const avgUtil = utils.length ? Math.round(utils.reduce((a, b) => a + b, 0) / utils.length) : null;

    // Groups/rows actually rendered — filtered to gap-owners when the Open gaps
    // tile is toggled on.
    const displayGroups = gapsOnly
        ? groups
              .map((r) => ({ ...r, consultants: r.consultants.filter((c) => (gapsByEmail[c.email] ?? 0) > 0) }))
              .filter((r) => r.consultants.length > 0)
        : groups;

    const metricTiles = [
        {
            label: gapsOnly ? "Open gaps · filtering" : "Open gaps",
            value: gaps,
            color: T.red,
            tip: gapsOnly
                ? "Showing only consultants with open gaps — click to clear"
                : `Weekdays from today with no visit and no leave/admin${
                      uncovered > 0 ? ` · excludes ${uncovered} consultant-days the summary window doesn't cover` : ""
                  } — click to show only the gaps`,
            onClick: gaps > 0 || gapsOnly ? () => setGapsOnly((g) => !g) : undefined,
            active: gapsOnly,
        },
        // "Single-visit days" exists to surface pairing candidates, so it goes with
        // the rest of that feature rather than standing on its own.
        ...(showPoints
            ? [{ label: "Single-visit days", value: singles, color: T.amber, tip: "Days from today with one visit — pairing candidates" }]
            : []),
        { label: "Visits booked", value: visitTotal, color: T.purpleDeep, tip: "Visits across the visible period" },
        { label: "Leave & admin days", value: offDays, color: T.muted, tip: "Days unavailable for visits" },
        {
            label: "Avg utilisation · days",
            value: avgUtil == null ? "—" : `${avgUtil}%`,
            color: T.purple,
            tip: "Days used (a visit, admin or training) against working days, averaged across the visible consultants for the period shown. Holiday and sickness come out of the working days.",
        },
    ] as Array<{ label: string; value: number | string; color: string; tip: string; onClick?: () => void; active?: boolean }>;

    const MetricsStrip = visibleConsultants.length > 0 && (
        <Box sx={{ mb: "14px" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
                {metricTiles.map((m) => (
                    <Box
                        key={m.label}
                        title={m.tip}
                        onClick={m.onClick}
                        sx={{
                            background: m.active ? "#FBF1EF" : T.card,
                            border: `1px solid ${m.active ? T.red : T.border}`,
                            borderRadius: "12px",
                            padding: "12px 14px",
                            cursor: m.onClick ? "pointer" : "default",
                            "&:hover": m.onClick ? { borderColor: T.red } : undefined,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                            <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", background: m.color, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: m.active ? T.red : T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {m.label}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: "22px", fontWeight: 800, color: T.ink2, mt: "4px", fontVariantNumeric: "tabular-nums" }}>
                            {m.value}
                        </Typography>
                    </Box>
                ))}
            </Box>
            {uncovered > 0 && (
                <Typography sx={{ fontSize: "11.5px", color: T.muted, mt: "8px" }}>
                    {uncovered} consultant-days in this period aren't covered by the visit summary (it returns a
                    28-day window), so they're excluded from these tiles rather than counted as free.
                </Typography>
            )}
            {gapsOnly && (
                <Typography sx={{ fontSize: "11.5px", color: T.red, fontWeight: 700, mt: "8px" }}>
                    Showing only consultants with open gaps ({displayGroups.flatMap((g) => g.consultants).length} of{" "}
                    {visibleConsultants.length}) — gap days highlighted. Click the tile again to clear.
                </Typography>
            )}
        </Box>
    );
    const hasRows = groups.some((g) => (g.consultants || []).length > 0);

    if (!hasRows)
        return (
            <Box sx={{ padding: "40px 28px", display: "flex", justifyContent: "center" }}>
                <Box sx={{ background: T.card, border: `1px dashed ${T.border}`, borderRadius: "12px", padding: "32px", maxWidth: "580px", textAlign: "center" }}>
                    <Typography sx={{ fontSize: "16px", fontWeight: 700, color: T.ink2 }}>No booked visits this week</Typography>
                    <Typography sx={{ fontSize: "13.5px", color: T.muted, mt: "8px", lineHeight: 1.5 }}>
                        Nothing is booked for {team === "AllTeams" ? "any team" : team}
                        {team === "AllTeams" ? "" : ` · ${team}`} in this week. This view only
                        shows visits already <b>allocated to a consultant</b> — anything still in the pool ("to be booked")
                        isn't shown here. Try the week arrows.
                    </Typography>
                </Box>
            </Box>
        );

    if (mode === "month") {
        const gridTemplate = showPoints
            ? `170px repeat(${dates.length}, minmax(24px, 1fr)) 90px`
            : `170px repeat(${dates.length}, minmax(24px, 1fr))`;
        return (
            <Box sx={{ padding: "24px 28px" }}>
                {MetricsStrip}
                <Box sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: "12px", overflowX: "auto" }}>
                    <Box sx={{ minWidth: `${170 + dates.length * 30 + (showPoints ? 90 : 0)}px`, padding: "0 0 4px" }}>
                        {/* header: weekday initial + day number */}
                        <Box sx={{ display: "grid", gridTemplateColumns: gridTemplate, gap: "3px", background: T.rowTint, padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
                            <Typography sx={{ padding: "0 10px", fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: T.muted, alignSelf: "center" }}>
                                Consultant
                            </Typography>
                            {dates.map((d, i) => (
                                <Box key={i} sx={{ textAlign: "center" }}>
                                    <Typography sx={{ fontSize: "8.5px", fontWeight: 800, color: T.muted2 }}>
                                        {d.toLocaleDateString("en-GB", { weekday: "narrow" })}
                                    </Typography>
                                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.muted, fontVariantNumeric: "tabular-nums" }}>
                                        {d.getDate()}
                                    </Typography>
                                </Box>
                            ))}
                            {showPoints && (
                            <Typography sx={{ paddingRight: "10px", fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: T.muted, textAlign: "right", alignSelf: "center" }}>
                                Total
                            </Typography>
                            )}
                        </Box>

                        {displayGroups.map((r) => (
                            <Box key={r.name}>
                                {showTeamHeaders && (
                                    <Box sx={{ background: "#F1F3F9", padding: "5px 12px", fontSize: "11px", fontWeight: 700, color: T.ink2 }}>
                                        {r.name}
                                    </Box>
                                )}
                                {r.consultants.map((c) => (
                                    <MonthRow
                                        key={c.email}
                                        c={c}
                                        dates={dates}
                                        gridTemplate={gridTemplate}
                                        onOpen={() => onOpen(r.name, c)}
                                        onJump={onJumpToDate}
                                    />
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Typography sx={{ fontSize: "11px", color: T.muted2, mt: "10px" }}>
                    Month heatmap: the number is <b>points</b> that day — <b style={{ color: T.purpleDeep }}>purple</b> booked,{" "}
                    <b style={{ color: T.green }}>green</b> completed, <b style={{ color: T.muted }}>grey</b> closed without
                    delivery, <b style={{ color: T.amber }}>amber</b> a proposal or a non-visit day —{" "}
                    <b>H</b>&nbsp;holiday, <b>A</b>&nbsp;admin, <b>T</b>&nbsp;training, <b>S</b>&nbsp;sickness.{" "}
                    <b style={{ color: T.amber }}>1⚡ (dashed)</b> = a single visit booked for ≤5h, so a pairing candidate —
                    travel and location are not assessed, so it is not a confirmed missed double. Click any day to open that
                    week's detailed view.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: "24px 28px" }}>
            {MetricsStrip}
            <Box sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: "12px", overflow: "hidden" }}>
                {/* header row */}
                <Box sx={{ display: "grid", gridTemplateColumns: gridFor(showPoints), background: T.rowTint, borderBottom: `1px solid ${T.border}` }}>
                    <Box sx={{ padding: "10px", fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: T.muted }}>
                        Consultant
                    </Box>
                    {weekDates.map((d, i) => (
                        <Box key={i} sx={{ padding: "8px 10px 6px", borderLeft: `1px solid ${T.border}` }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: T.muted }}>
                                {d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit" })}
                            </Typography>
                            {/* time scale for the day timeline strips */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", mt: "2px" }}>
                                {["08:00", "13:00", "18:00"].map((t) => (
                                    <Typography key={t} sx={{ fontSize: "8.5px", color: T.muted2, fontVariantNumeric: "tabular-nums" }}>
                                        {t}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    ))}
                    {showPoints && (
                        <Box sx={{ padding: "10px 10px 6px 4px", fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: T.muted, textAlign: "right", borderLeft: `1px solid ${T.border}` }}>
                            Total
                        </Box>
                    )}
                </Box>

                {displayGroups.map((r) => (
                    <Box key={r.name}>
                        {showTeamHeaders && (
                            <Box sx={{ background: "#F1F3F9", padding: "6px 12px", fontSize: "11.5px", fontWeight: 700, color: T.ink2 }}>
                                {r.name}
                            </Box>
                        )}
                        {r.consultants.map((c) => (
                            <ConsultantRow
                                key={c.email}
                                c={c}
                                weekDates={weekDates}
                                overrides={overrides}
                                onVisitClick={setSelectedVisit}
                                dayOverrides={dayOverrides}
                                onEditDay={(day, existingAdmin) =>
                                    setDayTarget({ consultantName: c.name, email: c.email, day, existing: existingAdmin })
                                }
                                onPlanDay={(day, existing) =>
                                    setPlannerTarget({ consultantName: c.name, email: c.email, day, existing })
                                }
                                highlightGaps={gapsOnly}
                                onOpen={() => onOpen(r.name, c)}
                            />
                        ))}
                    </Box>
                ))}
            </Box>

            <Typography sx={{ fontSize: "11px", color: T.muted2, mt: "10px" }}>
                The strip at the top of each day places visits on an <b>08:00–18:00</b> track (tick = 13:00) so
                morning vs afternoon — and double-stacked days — read at a glance. Chips show client · visit type ·
                🕘 booked window · ✓ actual times (colour = status: purple booked, green completed, amber proposal,
                grey closed). The bar underneath replays the day in order on the same axis —{" "}
                <b style={{ color: T.amber }}>travel</b> → <b style={{ color: T.green }}>on site</b> →{" "}
                <b style={{ color: T.amber }}>travel</b> → <b style={{ color: T.purple }}>write-up</b> — so you can
                see exactly how each day was made up. Click any visit for details, its Salesforce record and
                prototype editing; hover any block for times.
            </Typography>

            <VisitModal
                visit={selectedVisit}
                edited={!!(selectedVisit && overrides[selectedVisit.id])}
                onClose={() => setSelectedVisit(null)}
                onSave={applyEdit}
            />
            <AdminDayModal target={dayTarget} onClose={() => setDayTarget(null)} onApply={applyDayEdit} />
            <DayPlannerModal
                target={plannerTarget}
                onClose={() => setPlannerTarget(null)}
                onGoPool={onGoPool}
                onAddAdmin={() => {
                    if (!plannerTarget) return;
                    // One click — add the admin day directly, no second modal.
                    const iso = toISODate(plannerTarget.day);
                    applyDayEdit(plannerTarget.email, iso, iso, false);
                    setPlannerTarget(null);
                }}
            />
        </Box>
    );
};

export default ScheduleScreen;
